import {
  ReactCompareSlider,
  ReactCompareSliderImage,
  ReactCompareSliderHandle,
} from "react-compare-slider";
import { ChevronsLeftRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { useContent } from "@/content/ContentContext";

const videoEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
};

const Comparison = ({ item, index, caption }) => {
  const embed = videoEmbedUrl(item.video_url);
  return (
    <div data-testid={`comparison-${index}`}>
      <div className="mb-5 flex flex-wrap items-baseline gap-x-4 gap-y-1" data-testid={`comparison-title-${index}`}>
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
          Projekt {index + 1}
        </span>
        {item.title && (
          <h3 className="font-heading text-xl font-bold tracking-tight text-foreground md:text-2xl">
            {item.title}
          </h3>
        )}
      </div>
      <div className="relative overflow-hidden border border-foreground/10">
      <div className="aspect-[16/10] max-h-[75vh] w-full md:aspect-[16/9]">
        {item.video_url ? (
          embed ? (
            <iframe
              src={embed}
              title={item.title || `Video ${index + 1}`}
              className="h-full w-full"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video
              src={item.video_url}
              controls
              playsInline
              className="h-full w-full object-cover"
            />
          )
        ) : (
          <ReactCompareSlider
            itemOne={
              <ReactCompareSliderImage
                src={item.before_url}
                alt={`${item.title || "Projekt"} vor der Sanierung`}
                style={{ objectFit: "cover", filter: "saturate(0.7)" }}
              />
            }
            itemTwo={
              <ReactCompareSliderImage
                src={item.after_url}
                alt={`${item.title || "Projekt"} nach der Sanierung`}
                style={{ objectFit: "cover" }}
              />
            }
            handle={
              <ReactCompareSliderHandle
                buttonStyle={{
                  background: "hsl(15 80% 54%)",
                  border: "none",
                  color: "hsl(40 30% 97%)",
                  width: 52,
                  height: 52,
                  backdropFilter: "none",
                }}
                linesStyle={{ background: "hsl(40 30% 97%)", width: 2 }}
              />
            }
            style={{ width: "100%", height: "100%" }}
          />
        )}
        </div>
        {!item.video_url && (
          <>
            <span className="pointer-events-none absolute bottom-4 left-4 z-10 font-mono text-[10px] uppercase tracking-[0.25em] text-background/80 md:text-xs">
              Vorher
            </span>
            <span className="pointer-events-none absolute bottom-4 right-4 z-10 bg-primary px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-primary-foreground md:text-xs">
              Nachher
            </span>
          </>
        )}
        {!item.video_url && (
          <p className="pointer-events-none absolute bottom-4 left-1/2 z-10 hidden -translate-x-1/2 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-background/80 md:flex md:text-xs">
            <ChevronsLeftRight className="h-4 w-4" />
            {caption}
          </p>
        )}
      </div>
    </div>
  );
};

export const BeforeAfter = () => {
  const content = useContent();
  const { beforeAfter } = content;
  const items =
    beforeAfter.items && beforeAfter.items.length
      ? beforeAfter.items
      : [
          {
            id: "legacy",
            title: "",
            before_url: beforeAfter.before_url,
            after_url: beforeAfter.after_url,
            video_url: "",
          },
        ];

  return (
    <section
      id="vorher-nachher"
      data-testid="before-after-section"
      className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-40"
    >
      <Reveal>
        <p
          className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-primary"
          data-testid="before-after-chapter"
        >
          03 · Vorher / Nachher
        </p>
        <h2
          className="max-w-3xl font-heading text-4xl font-black tracking-tight text-foreground sm:text-5xl"
          data-testid="before-after-heading"
        >
          {beforeAfter.heading}
        </h2>
        <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
          {beforeAfter.text}
        </p>
      </Reveal>

      <div className="mt-14 space-y-10">
        {items.map((item, i) => (
          <Reveal key={item.id || i} delay={0.1}>
            <Comparison item={item} index={i} caption={beforeAfter.caption} />
          </Reveal>
        ))}
      </div>
    </section>
  );
};
